import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { useAppSelector } from '../store/hooks'
import { eventPathWithEmail } from '../utils/event'
import { useGetPublicEventQuery, useRegisterParticipantMutation } from '@proaxiv/server-api'
import { fromEventSummary } from './event/eventMapping'
import type { GetPublicEventResponse } from './event/eventMapping'
import { RegistrationFormView } from './event/RegistrationFormView'
import type { RegistrationFormValues } from './event/RegistrationFormView'

/**
 * Standalone registration page at /e/:id/register.
 * Resolves the real event and wires the registration API into {@link RegistrationFormView},
 * which owns all of the form's presentation. No AppLayout, no PROAXIV branding — purely
 * event-branded.
 */
export function RegisterPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const prefillEmail = searchParams.get('email')?.trim() ?? ''
  // Populated from a rejection email's "update & resubmit" link (see #215) so a previously
  // rejected applicant doesn't have to retype everything just to be re-evaluated. Also used by
  // invitation links, which only ever set ?email=.
  const prefillFirstName = searchParams.get('firstName')?.trim() ?? ''
  const prefillLastName = searchParams.get('lastName')?.trim() ?? ''
  const prefillCompany = searchParams.get('company')?.trim() ?? ''
  // Consents already given before a rejection shouldn't have to be re-ticked on resubmit —
  // only ever pre-checks (never pre-declines), so a consent the applicant hadn't given still
  // requires an explicit, active choice.
  const prefillConsentPersonal = searchParams.get('consentPersonal') === '1'
  const prefillConsentMedia = searchParams.get('consentMedia') === '1'

  // Prefer the event from the Redux store when already loaded (e.g. organiser view).
  // Fall back to the API so direct URL navigation works for public visitors.
  const eventFromStore = useAppSelector((s) => s.events.list.find((e) => e.id === id))
  // Store data is authoritative (no API round-trip needed) once the event is published
  // or already flagged as a draft-preview.
  const haveStoreEvent =
    eventFromStore?.status === 'published' || eventFromStore?.draftPreview === true
  const {
    data: rawData,
    isLoading,
    isFetching,
    isUninitialized,
  } = useGetPublicEventQuery(
    { eventId: id ?? '' },
    // Always fetch from API for draft store events so we receive the backend's draftPreview flag.
    // Skip only for already-published store events (no extra info needed from the API).
    { skip: !id || (!!eventFromStore && eventFromStore.status === 'published') }
  )
  const rawResponse = rawData as GetPublicEventResponse | undefined
  // draftPreview is authoritative from the API response (backend verified org membership).
  // For store events that already carry draftPreview:true (e.g. set by a prior API call),
  // that field is also respected. Store-only draft events without the flag show 404 as usual.
  const draftPreview = rawResponse?.draftPreview === true || eventFromStore?.draftPreview === true
  // Use the store event for display (published or already flagged as draft-preview).
  // For all other cases fall through to the API response.
  const event =
    (eventFromStore?.status === 'published' || eventFromStore?.draftPreview
      ? eventFromStore
      : undefined) ??
    (rawResponse ? fromEventSummary(rawResponse.event, rawResponse.draftPreview) : undefined)
  // Until the request has actually settled (data or error), keep showing the loading
  // state rather than a premature 404 — RTK Query reports isLoading:false for the
  // brief moment before an uninitialized/in-flight query is reflected in the hook.
  const queryPending = !!id && !haveStoreEvent && (isUninitialized || isLoading || isFetching)

  const [registerParticipant] = useRegisterParticipantMutation()

  async function handleSubmit(values: RegistrationFormValues) {
    if (!id) return
    try {
      await registerParticipant({
        eventId: id,
        registerBody: values,
      }).unwrap()
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status
      const code = (err as { data?: { code?: string } })?.data?.code
      if (code === 'REGISTRATION_REJECTED') {
        throw new Error(t('reg_err_rejected'), { cause: err })
      } else if (status === 409) {
        throw new Error(t('reg_err_duplicate'), { cause: err })
      } else if (status === 400) {
        throw new Error(t('reg_err_invalid'), { cause: err })
      } else {
        throw new Error(t('reg_err_generic'), { cause: err })
      }
    }
  }

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (queryPending) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-neutral-950 text-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
      </div>
    )
  }

  // ── Not found / not published ─────────────────────────────────────────────────
  // Draft events are shown to authenticated org members (draftPreview = true); all other
  // non-published events — and any request without a valid org-member token — show 404.
  if (!event || (event.status !== 'published' && !draftPreview)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-neutral-950 text-white">
        <p className="text-5xl font-black tracking-tighter opacity-20">404</p>
        <p className="text-sm text-neutral-400">This event is not available.</p>
      </div>
    )
  }

  return (
    // The language switcher lives here rather than inside RegistrationFormView, because that
    // view is also rendered inside the event editor's live-preview panel — a viewport-fixed
    // control belongs to the real registration page only (TC-EVT-062).
    <>
      <div className="fixed top-4 right-4 z-40">
        <Suspense fallback={null}>
          <LanguageSwitcher align="right" />
        </Suspense>
      </div>
      <RegistrationFormView
        event={event}
        prefillEmail={prefillEmail}
        prefillFirstName={prefillFirstName}
        prefillLastName={prefillLastName}
        prefillCompany={prefillCompany}
        prefillConsentPersonal={prefillConsentPersonal}
        prefillConsentMedia={prefillConsentMedia}
        backHref={eventPathWithEmail(id ?? '', '', prefillEmail)}
        onSubmit={handleSubmit}
      />
    </>
  )
}
